<template>
  <div ref="gridRef" class="mosaic-grid" :style="{ gap: `${gap}px` }">
    <div
      v-for="(column, columnIndex) in columns"
      :key="columnIndex"
      class="mosaic-grid__column"
      :data-index="columnIndex"
      :style="{ gap: `${gap}px` }"
    >
      <div
        v-for="itemIndex in column"
        :key="itemIndex"
        class="mosaic-grid__item"
        :style="colWidth ? { height: colWidth / widthToHeightRatio(items[itemIndex]) + 'px' } : undefined"
      >
        <slot :item="(items[itemIndex])" :index="itemIndex" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { GalleryItem } from '@/store/gallery/types'
import {
  nextTick,
  onMounted,
  ref,
  watch
} from 'vue'

type Column = number[]

const props = withDefaults(
  defineProps<{
    numCols?: number
    items: GalleryItem[]
    gap?: number
  }>(),
  {
    numCols: 1,
    gap: 0
  }
)

const columns = ref<Column[]>([])
const gridRef = ref<HTMLDivElement | null>(null)
const colWidth = ref(0)

onMounted(() => {
  // This observer is required to calculate the column width based of the size of the grid. 
  // getBoundingClientRect returns zero if display:none, so we need to set the colWidth on the 
  // first instance when width is not zero.
  if (gridRef.value) {
    new ResizeObserver((entries: ResizeObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.contentRect.width) {
          // Ensure the boundingClientRect width is not zero
          const newColWidth = (entry.contentRect.width + props.gap) / props.numCols - props.gap
          
          // Only redraw when the colWidth has changed
          if (newColWidth !== colWidth.value){
            colWidth.value = newColWidth 
            redraw()            
          }
        }
      })
    }).observe(gridRef.value)
  }

})

watch(() => props.items.length, async (newItemsLength, prevItemsLength) => {
  if (columns.value.length > 0 && newItemsLength >  prevItemsLength) {
    // Columns need to have at least one item rendered to calculate the target to render the next one. 
    await fillColumns(prevItemsLength)
  } else {
    redraw()
  }
})

const redraw = async () => {
  columns.value = createColumns()
  await fillColumns(0)
}

const createColumns = (): Column[] => {
  return [...new Array(props.numCols)].map(() => [])
}

/**
 * Recursively add item to shortest column
 * @param itemIndex
 */
const fillColumns = async (itemIndex: number) => {
  if (itemIndex >= props.items.length) {
    return
  }

  // Wait for columns divs to be rendered
  await nextTick()
  if (gridRef.value) {
    const columnDivs = [...gridRef.value.children] as HTMLDivElement[]

    const target = columnDivs.reduce((prev, curr) =>
      curr.getBoundingClientRect().height < prev.getBoundingClientRect().height
        ? curr
        : prev
    )
    if (target.dataset.index !== undefined) {
      columns.value[+target.dataset.index].push(itemIndex)
      await fillColumns(itemIndex + 1)
    }
  }
}

/**
 *  Required to get the correct aspect ratio as preview video can have a different aspect ratio from the actual video
 */
const widthToHeightRatio = (item: GalleryItem) => {
  if (item.preview.videoSource) {
    if (item.preview.videoSource.height && item.preview.videoSource.width) {
      return item.preview.videoSource.width / item.preview.videoSource.height
    }
  }
  return item.widthToHeightRatio
}

</script>

<style lang="scss">
.mosaic-grid {
  display: flex;

  &__column {
    display: flex;
    flex-basis: 0;
    flex-direction: column;
    flex-grow: 1;
    height: max-content;
  }

  &__item {
    cursor: pointer;
  }
}
</style>
